import type { AnalyticsEvent, EventLimit } from './analytics';
import type { DeviceStorage } from './deviceStorage';

/*
  Sending events, and remembering which ones have already been sent enough times.

  Some events are worth exactly one emission per install — "first ever move" is an account-level
  conversion, not a per-puzzle one — and some are worth one per board per day. That's a property of
  the event rather than of a call site, so the events say it (`limit`) and this enforces it in the one
  place, instead of each caller wrapping itself in a once-only helper it has to remember to apply.

  Storage is injected and **may be asynchronous**, which is the whole point: analytics is
  fire-and-forget, so nothing is waiting on the answer, and a client whose only persistent storage is
  async (React Native) can enforce the same limits a browser does with localStorage. Claims are
  serialised through one chain, so two moves in the same tick can't both read a count of zero and
  both send.
*/

/**
 * The storage key a counter lives under.
 *
 * The prefix is the one the website has always used. It reads oddly now that the helper it was named
 * after is gone, but changing it would reset every counter in every browser and re-fire a
 * "first ever" event for the entire existing audience.
 */
export const eventCountKey = (key: string) => `callWithPersistentStorage_${key}`;

/**
 * Counts before this API existed were written as the string `true` by a once-only helper. Anything
 * unparseable means "already sent as many times as it was ever going to be".
 */
const countFrom = (value: string | null): number => {
  if (value === null) return 0;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
};

export interface EventTrackerOptions {
  /** Where the counters are kept. */
  storage: DeviceStorage;
  /** Where an event goes once it's cleared to be sent. */
  send: (event: AnalyticsEvent) => void;
}

export type EventTracker = ReturnType<typeof createEventTracker>;

export function createEventTracker({ storage, send }: EventTrackerOptions) {
  // One chain for everything, so a claim always sees the writes of the claims before it.
  let queue: Promise<unknown> = Promise.resolve();
  const serialise = <T>(work: () => Promise<T>): Promise<T> => {
    const next = queue.then(work, work);
    // A failure must not poison the chain for everything after it.
    queue = next.catch(() => {});
    return next;
  };

  /** Takes one of the event's remaining emissions, or reports that there are none left. */
  async function claim({ key, times = 1 }: EventLimit): Promise<boolean> {
    const storageKey = eventCountKey(key);
    const count = countFrom(await storage.get(storageKey));
    if (count >= times) return false;

    await storage.set(storageKey, String(count + 1));
    return true;
  }

  return {
    /**
     * Sends an event, if its limit allows. Returns nothing: the caller is not waiting, and an
     * analytics failure is never worth surfacing into a screen.
     *
     * `onSent` runs only on an emission that actually happened, so a side effect hung off an event —
     * the website's ad conversion on the first-ever completion — can't fire on a suppressed one.
     */
    track(event: AnalyticsEvent, onSent?: () => void) {
      const emit = () => {
        send(event);
        onSent?.();
      };

      if (!event.limit) {
        emit();
        return;
      }

      void serialise(async () => {
        if (await claim(event.limit!)) emit();
      }).catch(() => {});
    },

    /**
     * Drops the counters under `prefix`, except the one named by `except`.
     *
     * What this is for: a counter keyed by board keeps one entry per board forever otherwise. The
     * caller sweeps the counters it knows are stale — the same board's earlier dates — as it records
     * the current one.
     */
    forget(prefix: string, { except }: { except?: string } = {}) {
      const wanted = except === undefined ? undefined : eventCountKey(except);

      void serialise(async () => {
        const keys = await storage.keys();
        for (const key of keys) {
          if (key.startsWith(eventCountKey(prefix)) && key !== wanted) await storage.remove(key);
        }
      }).catch(() => {});
    },
  };
}
